import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import * as Sentry from '@sentry/react';

/**
 * TELV1-489 — errorElement for the data router. React Router catches route
 * render errors with its own boundary BEFORE any boundary wrapping
 * RouterProvider can see them, so without this element users got React
 * Router's default stack-trace page and Sentry never received the error.
 * Reports once per error, then renders a branded recovery screen.
 *
 * Deliberately self-contained (no theme/store/asset imports): it renders
 * above ThemeWrapperDashboard, where no app context exists, and must not be
 * able to crash itself.
 */
function RouteErrorFallback() {
  const error = useRouteError();

  useEffect(() => {
    Sentry.captureException(error, { tags: { error_boundary: 'router' } });
  }, [error]);

  return (
    <div className="relative flex items-center justify-center min-h-screen px-4 mx-auto text-center">
      <div className="flex flex-col items-center justify-center gap-2 mx-auto text-center">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50">
          <svg
            className="w-8 h-8 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
        </div>
        <h1 className="text-5xl font-bold mt-8 text-dark min500:text-4xl">Something went wrong</h1>
        <p className="mt-2 text-lg font-medium text-light-dark">
          An unexpected error occurred. Please reload the page to continue.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center h-10 px-4 py-2 mt-5 text-sm font-medium rounded-md text-neutral-50 bg-primary transition-all ease-in-out hover:bg-darker-primary"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

export default RouteErrorFallback;
